// Utilidades para formatação e tratamento de pix
import { formatCnpjString, isAlphanumericCnpj } from "./validateCnpj";
import { isValidCpf } from "./validateCpf";

export function extractCheckPixErrorObject(error: any): {
    code?: string;
    description: string;
  } {
    try {
      const respData = error?.response?.data;
      if (Array.isArray(respData) && respData.length > 0) {
        const first = respData[0] || {};
        const rawMsg: string = String(first.message ?? "");
        const quotedJsonMatch = rawMsg.match(/"(\{.*\})"$/);
        if (quotedJsonMatch && quotedJsonMatch[1]) {
          const escaped = quotedJsonMatch[1];
          const unescaped = escaped.replace(/\\"/g, '"');
          const json = JSON.parse(unescaped);
          const err = json?.errors?.[0] || {};
          const description = err?.description || err?.title;
          const code = err?.code || err?.title;
          if (description) return { code, description };
        }
        // Fallback
        const msgNoStatus = rawMsg.replace(/^\d+\s*-\s*/, "");
        return {
          description: msgNoStatus || "Erro ao validar dados do pagamento PIX",
        };
      }
      const dataMsg = error?.response?.data?.message;
      if (typeof dataMsg === "string" && dataMsg.length > 0)
        return { description: dataMsg };
      const errMsg = error?.message;
      if (typeof errMsg === "string" && errMsg.length > 0)
        return { description: errMsg };
    } catch (_) {
      // ignore parsing errors
    }
    return { description: "Erro ao validar dados do pagamento PIX" };
}

export function detectPixKeyType(val: string) {
  if (isAlphanumericCnpj(val)) {
      return "CNPJ";
  }
  const numeric = val.replace(/\D/g, "");
  if (numeric.length === 11) {
      if (isValidCpf(numeric)) {
          return "CPF";
      }
      const areaCode = numeric.substring(0, 2);
      const areaCodeNum = parseInt(areaCode);
      if (areaCodeNum >= 11 && areaCodeNum <= 99 && numeric.charAt(2) === '9') {
          return "PHONE";
      }
      return "CPF";
  }
  if (numeric.length === 10) {
      const areaCode = numeric.substring(0, 2);
      const areaCodeNum = parseInt(areaCode);
      if (areaCodeNum >= 11 && areaCodeNum <= 99) {
          return "PHONE";
      }
  }
  if (numeric.length === 14) return "CNPJ";
  return "OTHER";
}

export function applyPixMask(val: string) {
  const numeric = val.replace(/\D/g, "");
  if (numeric.length === 0) return val;
  // Pix CC
  if (numeric.startsWith("000201") && numeric.length > 40) {
      return numeric;
  }
  const type = detectPixKeyType(val);

  if (type === "OTHER") {
      return val;
  }
  if (type === "CPF") {
      return numeric
          .slice(0, 11)
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d)/, "$1.$2")
          .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  if (type === "PHONE") {
      if (numeric.length <= 2) {
          return `(${numeric}`;
      } else if (numeric.length <= 6) {
          return numeric.replace(/^(\d{2})(\d)/, "($1) $2");
      } else if (numeric.length === 10) {
          return numeric
              .replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3");
      } else if (numeric.length === 11) {
          return numeric
              .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
      } else if (numeric.length > 6 && numeric.length < 10) {
          return numeric.replace(
              /^(\d{2})(\d{4})(\d)/,
              "($1) $2-$3"
          );
      }
  }
  if (type === "CNPJ") {
      return formatCnpjString(val);
  }
  return numeric;
}

export function getBankNameByIspb(ispb?: string): string {
    if (!ispb) return "—";
    // Mapeamento dos principais códigos ISPB para nomes de bancos
    const ispbMap: Record<string, string> = {
      "00000000": "Banco do Brasil S.A.",
      "00038121": "Banco Central do Brasil - Selic",
      "00038166": "Banco Central do Brasil",
      "00250699": "AGK Corretora de Câmbio S.A.",
      "00315557": "Confederação Nacional das Cooperativas Centrais Unicred Ltda.",
      "00360305": "Caixa Econômica Federal",
      "00394460": "Secretaria do Tesouro Nacional - STN",
      "00416968": "Banco Inter S.A.",
      "00517645": "Banco Ribeirão Preto S.A.",
      "00556603": "Banco Bari de Investimentos e Financiamentos S.A.",
      "00558456": "Banco Cetelem S.A.",
      "00795423": "Banco Semear S.A.",
      "00806535": "Planner Corretora de Valores S.A.",
      "00997185": "Banco B3 S.A.",
      "01023570": "Banco Rabobank International Brasil S.A.",
      "01027058": "Cielo S.A.",
      "01073966":
        "Cooperativa de Crédito Rural de Abelardo Luz - Sulcredi/Crediluz",
      "01181521": "Banco Cooperativo Sicredi S.A.",
      "01330387":
        "Cooperativa de Crédito Rural de Pequenos Agricultores e da Reforma Agrária do CE",
      "01522368": "Banco BNP Paribas Brasil S.A.",
      "01634601":
        "Central de Cooperativas de Economia e Crédito Mútuo do Estado do RS",
      "01658426": "Cooperforte - Cooperativa de Economia e Crédito Mútuo",
      "01701201": "Kirton Bank S.A. - Banco Múltiplo",
      "01800019": "Portocred S.A. - Crédito, Financiamento e Investimento",
      "01852137": "BBC Leasing S.A. - Arrendamento Mercantil",
      "02038232": "Banco Cooperativo do Brasil S.A. - Bancoob",
      "02276653":
        "Trinus Capital Distribuidora de Títulos e Valores Mobiliários S.A.",
      "02318507": "Banco KEB Hana do Brasil S.A.",
      "02332886":
        "XP Investimentos Corretora de Câmbio, Títulos e Valores Mobiliários S/A",
      "02398976": "Uniprime Norte do Paraná - Cooperativa de Crédito Ltda",
      "02685483":
        "CM Capital Markets Corretora de Câmbio, Títulos e Valores Mobiliários Ltda",
      "02801938": "Banco Morgan Stanley S.A.",
      "02819125":
        "UBS Brasil Corretora de Câmbio, Títulos e Valores Mobiliários S.A.",
      "02992317": "Treviso Corretora de Câmbio S.A.",
      "02992335": "Câmara Interbancária de Pagamentos - CIP - LDL",
      "03012230": "Hipercard Banco Múltiplo S.A.",
      "03017677": "Banco J. Safra S.A.",
      "03046391":
        "Uniprime Central - Central Interestadual de Cooperativas de Crédito Ltda.",
      "03215790": "Banco Toyota do Brasil S.A.",
      "03311443": "Parati - Crédito, Financiamento e Investimento S.A.",
      "03323840": "Banco Alfa S.A.",
      "03502968": "PI Distribuidora de Títulos e Valores Mobiliários S.A.",
      "03532415": "Banco ABN Amro S.A.",
      "03609817": "Banco Cargill S.A.",
      "03751794":
        "Terra Investimentos Distribuidora de Títulos e Valores Mobiliários Ltda.",
      "03973814":
        "Servicoop - Cooperativa de Crédito dos Servidores Públicos Estaduais do RGS",
      "04062902": "Vision S.A. Corretora de Câmbio",
      "04184779": "Banco Bradescard S.A.",
      "04257795": "Nova Futura Corretora de Títulos e Valores Mobiliários Ltda.",
      "04307598": "Fidúcia Sociedade de Crédito ao Microempreendedor",
      "04332281": "Goldman Sachs do Brasil Banco Múltiplo S.A.",
      "04391007": "Câmara Interbancária de Pagamentos",
      "04632856": "Credisis - Central de Cooperativas de Crédito Ltda.",
      "04715685":
        "Cooperativa de Crédito Mútuo dos Despachantes de Trânsito de SC e RO",
      "04814563": "Sorocred Crédito, Financiamento e Investimento S.A.",
      "04866275": "Banco Inbursa S.A.",
      "04902979": "Banco da Amazônia S.A.",
      "04913129": "Confidence Corretora de Câmbio S.A.",
      "04913711": "Banco do Estado do Pará S.A.",
      "05351887": "Zema Crédito, Financiamento e Investimento S/A",
      "05442029":
        "Casa do Crédito S.A. Sociedade de Crédito ao Microempreendedor",
      "05463212": "Cooperativa Central de Crédito - Ailos",
      "05790149":
        "Central Cooperativa de Crédito no Estado do Espírito Santo - Cecoop",
      "06271464": "Banco Bradesco BBI S.A.",
      "07207996": "Banco Bradesco Financiamentos S.A.",
      "07237373": "Banco do Nordeste do Brasil S.A.",
      "07450604": "China Construction Bank (Brasil) Banco Múltiplo S/A",
      "07512441": "HS Financeira S/A Crédito, Financiamento e Investimentos",
      "07652226": "Lecca Crédito, Financiamento e Investimento S/A",
      "07656500": "Banco KDB do Brasil S.A.",
      "07679404": "Banco Topázio S.A.",
      "07853842": "Cooperativa de Crédito Rural de Ouro Sulcredi/Ouro",
      "07945233": "Pólocred Sociedade de Crédito ao Microempreendedor",
      "08240446": "Cooperativa de Crédito Rural de Ibiam - Sulcredi/Ibiam",
      "08253539":
        "Cooperativa de Crédito Rural de São Miguel do Oeste - Sulcredi/São Miguel",
      "08357240": "Banco CSF S.A.",
      "08561701": "Pagseguro Internet S.A.",
      "08609934": "Moneycorp Banco de Câmbio S.A.",
      "09089356": "Gerencianet S.A.",
      "09105360":
        "ICAP do Brasil Corretora de Títulos e Valores Mobiliários Ltda.",
      "09210106": "Socred S.A. - Sociedade de Crédito ao Microempreendedor",
      "09274232": "State Street Brasil S.A. - Banco Comercial",
      "09313766":
        "Caruana S.A. - Sociedade de Crédito, Financiamento e Investimento",
      "09512542": "Codepe Corretora de Valores e Câmbio S.A.",
      "09516419": "Banco Original do Agronegócio S.A.",
      "09554480": "Super Pagamentos e Administração de Meios Eletrônicos S.A.",
      "10264663": "BancoSeguro S.A.",
      "10398952": "Confederação Nacional das Cooperativas Centrais de Crédito",
      "10573521": "Mercadopago.com Representações Ltda.",
      "10664513": "Banco Agibank S.A.",
      "10690848": "Banco da China Brasil S.A.",
      "10853017": "Get Money Corretora de Câmbio S.A.",
      "10866788": "Banco Bandepe S.A.",
      "11165756": "Global Finanças Sociedade de Crédito ao Microempreendedor",
      "11476673": "Banco Randon S.A.",
      "11495073": "OM Distribuidora de Títulos e Valores Mobiliários Ltda",
      "11581339": "Money Plus Sociedade de Crédito ao Microempreendedor",
      "11703662": "Travelex Banco de Câmbio S.A.",
      "11758741": "Banco Finaxis S.A.",
      "11970623": "Senff S.A. - Crédito, Financiamento e Investimento",
      "12865507": "BRK S.A. Crédito, Financiamento e Investimento",
      "13009717": "Banco do Estado de Sergipe S.A.",
      "13059145": "Bexs Banco de Câmbio S/A",
      "13140088": "Acesso Soluções de Pagamento S.A.",
      "13220493": "BR Partners Banco de Investimento S.A.",
      "13293225": "Órama Distribuidora de Títulos e Valores Mobiliários S.A.",
      "13370835": "BPP Instituição de Pagamento S.A.",
      "13486793": "BRL Trust Distribuidora de Títulos e Valores Mobiliários S.A.",
      "13673855":
        "Fram Capital Distribuidora de Títulos e Valores Mobiliários S.A.",
      "13720915": "Banco Western Union do Brasil S.A.",
      "13884775": "Hub Pagamentos S.A",
      "14190547": "Cambionet Corretora de Câmbio Ltda.",
      "14388334": "Paraná Banco S.A.",
      "14511781": "Bari Companhia Hipotecária",
      "15114366": "Banco Bocom BBM S.A.",
      "15173776": "Banco Capital S.A.",
      "15357060": "Banco Woori Bank do Brasil S.A.",
      "15581638": "Facta Financeira S.A. - Crédito Financiamento e Investimento",
      "16501555": "Stone Pagamentos S.A.",
      "16927221": "Amazônia Corretora de Câmbio Ltda.",
      "16944141": "Broker Brasil Corretora de Câmbio Ltda.",
      "17184037": "Banco Mercantil do Brasil S.A.",
      "17298092": "Banco Itaú BBA S.A.",
      "17351180": "Banco Triângulo S.A.",
      "17352220": "Senso Corretora de Câmbio e Valores Mobiliários S.A",
      "17453575": "ICBC do Brasil Banco Múltiplo S.A.",
      "17772370": "Vip's Corretora de Câmbio Ltda.",
      "17826860": "MS Sociedade de Crédito ao Microempreendedor",
      "18188384": "Crefaz Sociedade de Crédito ao Microempreendedor",
      "18236120": "Nu Pagamentos S.A. (Nubank)",
      "18520834": "UBS Brasil Banco de Investimento S.A.",
      "19307785": "MS Bank S.A. Banco de Câmbio",
      "20155248": "Parmetal Distribuidora de Títulos e Valores Mobiliários Ltda",
      "21018182": "Boletobancário.com Tecnologia de Pagamentos Ltda.",
      "22896431": "PicPay Serviços S.A.",
      "26563270": "PicPay Serviços S.A.",
      "30723886": "Banco Modal S.A.",
      "31680151": "SWAP",
      "31872495": "C6 Bank S.A.",
      "31880826": "Banco Guanabara S.A.",
      "31895683": "Banco Industrial do Brasil S.A.",
      "32402502": "Modal S.A.",
      "59588111": "Banco Neon S.A.",
      "60701190": "Itaú Unibanco S.A.",
      "60746948": "Banco Bradesco S.A.",
      "90400888": "Banco Santander (Brasil) S.A.",
    };
  
    return ispbMap[ispb] || `Banco (ISPB: ${ispb})`;
}

export function normalizePhoneForAPI(pixKey: string): string {
  if (!pixKey) return pixKey;
  if (pixKey.startsWith('+55')) {
    return pixKey;
  }
  const cleanKey = pixKey.replace(/\D/g, '');
  
  if (cleanKey.length === 10 || cleanKey.length === 11) {
    const areaCode = cleanKey.substring(0, 2);
    if (parseInt(areaCode) >= 11 && parseInt(areaCode) <= 99) {
      if (cleanKey.length === 11) {
        const thirdDigit = cleanKey.charAt(2);
        if (thirdDigit === '9') {
          return `+55${cleanKey}`;
        }
      }
      else if (cleanKey.length === 10) {
        return `+55${cleanKey}`;
      }
    }
  }
  return pixKey;
}

export function validatePIXKey(
  pixKey: string,
  _keyType?: string
): {
  isValid: boolean;
  detectedType?: string;
  errorMessage?: string;
} {
  if (!pixKey || pixKey.trim().length === 0) {
    return {
      isValid: false,
      errorMessage: "Chave PIX é obrigatória",
    };
  }

  // Clean the PIX key before validation, but preserve email format
  let cleanKey = pixKey.trim();

  // If it's not an email (no @), remove formatting characters
  if (!cleanKey.includes("@")) {
    cleanKey = cleanKey.replace(/[.\-/\s()]/g, "");
  }

  // CPF validation (11 digits)
  if (/^\d{11}$/.test(cleanKey)) {
    return {
      isValid: true,
      detectedType: "CPF",
    };
  }

  // CNPJ validation (14 digits)
  if (/^\d{14}$/.test(cleanKey)) {
    return {
      isValid: true,
      detectedType: "CNPJ",
    };
  }

  if (isAlphanumericCnpj(cleanKey)) {
    return {
      isValid: true,
      detectedType: "CNPJ",
    };
  }

  // Email validation
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanKey)) {
    return {
      isValid: true,
      detectedType: "EMAIL",
    };
  }

  // Phone validation (+5511999999999)
  if (/^\+55\d{10,11}$/.test(cleanKey)) {
    return {
      isValid: true,
      detectedType: "PHONE",
    };
  }

  // Random key validation (UUID format)
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      cleanKey
    )
  ) {
    return {
      isValid: true,
      detectedType: "RANDOM_KEY",
    };
  }

  return {
    isValid: false,
    errorMessage: "Formato de chave PIX inválido",
  };
}


/* formata data */
export function formatDateLabel(date: Date) {
  if (!date) return "Hoje";
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  const formatted = date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return isToday ? `Hoje ${formatted}` : formatted;
}

function parseEMV(data: string) {
  const result: Record<string, any> = {};
  let index = 0;

  while (index + 4 <= data.length) {
    const id = data.slice(index, index + 2);
    const length = Number(data.slice(index + 2, index + 4));

    if (Number.isNaN(length)) break;

    const valueStart = index + 4;
    const valueEnd = valueStart + length;

    if (valueEnd > data.length) break;

    const value = data.slice(valueStart, valueEnd);
    index = valueEnd;

    // Campos podem se repetir (ex: 26)
    if (result[id]) {
      if (Array.isArray(result[id])) {
        result[id].push(value);
      } else {
        result[id] = [result[id], value];
      }
    } else {
      result[id] = value;
    }
  }

  return result;
}

export function parsePixQRCode(qrCode: string) {
  try {
    if (!qrCode || typeof qrCode !== "string") {
      return { success: false, error: "QR Code inválido" };
    }

    const cleanQR = qrCode.trim();

    if (!cleanQR.startsWith("000201")) {
      return { success: false, error: "Formato de QR Code PIX inválido" };
    }

    const emv = parseEMV(cleanQR);

    // 💰 Valor do PIX
    const amount =
      emv["54"] !== undefined ? Number(emv["54"]) : undefined;
    console.log("amount", amount);
    // 🏪 Merchant
    const merchantName = emv["59"];
    const merchantCity = emv["60"];

    // 🧾 Descrição (campo 62 → subcampo 05)
    let description: string | undefined;
    if (emv["62"]) {
      const additional = parseEMV(emv["62"]);
      description = additional["05"];
    }

    // 🔑 Chave PIX (campo 26 → subcampo 01/25/26)
    let pixKey: string | undefined;
    const merchantInfo = emv["26"];

    if (merchantInfo) {
      const parsed26 = parseEMV(
        Array.isArray(merchantInfo) ? merchantInfo[0] : merchantInfo
      );

      pixKey =
        parsed26["25"] ||
        parsed26["26"] ||
        parsed26["01"];
    }

    return {
      success: true,
      amount,
      pixKey,
      description,
      merchantName,
      merchantCity,
    };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || "Erro ao processar QR Code PIX",
    };
  }
}
